/**
 * Chart Gallery Component
 * =======================
 * Displays generated charts from Excel analysis
 */

import React from 'react';

import {
  BarChart3,
  LineChart,
  PieChart,
  TrendingUp,
  TrendingDown,
  Activity,
  Image,
} from 'lucide-react';

export default function ChartGallery({ charts, intelligence }) {
  // Get trends from intelligence for visualization
  const trends = intelligence?.trends || [];

  if ((!charts || charts.length === 0) && trends.length === 0) {
    return (
      <div className="chart-gallery-empty">
        <BarChart3 className="w-12 h-12 text-slate-300" />
        <p>No charts available yet</p>
        <span className="hint">
          Ask questions like "Show me a chart of sales by month" to generate visualizations
        </span>
      </div>
    );
  }

  return (
    <div className="chart-gallery">
      {/* Trend Cards */}
      {trends.length > 0 && (
        <div className="trends-section">
          <h4 className="section-title">
            <Activity className="w-4 h-4" />
            Trends Detected
          </h4>
          <div className="trends-grid">
            {trends.map((trend, idx) => (
              <TrendCard key={idx} trend={trend} />
            ))}
          </div>
        </div>
      )}

      {/* Charts Grid */}
      {charts && charts.length > 0 && (
        <div className="charts-section">
          <h4 className="section-title">
            <BarChart3 className="w-4 h-4" />
            Generated Charts
          </h4>
          <div className="charts-grid">
            {charts.map((chart, idx) => (
              <ChartCard key={idx} chart={chart} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TrendCard({ trend }) {
  const isPositive = trend.direction === 'increasing';
  const isNegative = trend.direction === 'decreasing';

  return (
    <div className={`trend-card ${trend.direction}`}>
      <div className="trend-header">
        <span className="trend-metric">{trend.metric}</span>
        {isPositive && <TrendingUp className="w-4 h-4 text-green-500" />}
        {isNegative && <TrendingDown className="w-4 h-4 text-red-500" />}
        {!isPositive && !isNegative && <Activity className="w-4 h-4 text-slate-400" />}
      </div>

      <div className="trend-value">
        <span className={`change ${isPositive ? 'positive' : isNegative ? 'negative' : ''}`}>
          {trend.changePercent > 0 ? '+' : ''}
          {trend.changePercent?.toFixed(1)}%
        </span>
      </div>

      <p className="trend-summary">{trend.summary}</p>

      {/* Mini sparkline representation */}
      {trend.data && trend.data.length > 0 && (
        <div className="trend-sparkline">
          {trend.data.slice(-10).map((point, idx) => {
            const maxVal = Math.max(...trend.data.map(d => d.total || 0));
            const height = maxVal > 0 ? ((point.total || 0) / maxVal) * 100 : 0;
            return (
              <div
                key={idx}
                className="sparkline-bar"
                style={{ height: `${Math.max(height, 4)}%` }}
                title={`${point.period}: ${point.total?.toLocaleString()}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function ChartCard({ chart }) {
  const ChartIcon = getChartIcon(chart.type);

  return (
    <div className="chart-card">
      <div className="chart-header">
        <ChartIcon className="w-4 h-4" />
        <span className="chart-type">{chart.type}</span>
      </div>

      {/* If chart has base64 image */}
      {chart.image_base64 ? (
        <div className="chart-image">
          <img src={`data:image/png;base64,${chart.image_base64}`} alt={`${chart.type} chart`} />
        </div>
      ) : (
        /* Placeholder for charts without rendered image */
        <div className="chart-placeholder">
          <Image className="w-8 h-8 text-slate-300" />
          <span>Chart preview</span>
        </div>
      )}

      {/* Chart data summary */}
      {chart.data && (
        <div className="chart-data-summary">
          <span className="data-points">
            {Array.isArray(chart.data) ? chart.data.length : 0} data points
          </span>
        </div>
      )}
    </div>
  );
}

function getChartIcon(type) {
  switch (type?.toLowerCase()) {
    case 'bar':
      return BarChart3;
    case 'line':
      return LineChart;
    case 'pie':
      return PieChart;
    default:
      return BarChart3;
  }
}
